import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tent, LogOut, MapPin, ChevronUp, ChevronDown, X } from 'lucide-react';
import ExtendStayControl from './ExtendStayControl';

/**
 * Vedvarende innsjekk/utsjekk-statuspill over kartet. Én flate for hele
 * innsjekk-tilstanden (erstatter spredte banner/pill/ark-rader):
 *
 *  - `checkin`: brukeren står ved en innsjekkbar plass, ikke innsjekket →
 *    «Sjekk inn på X?» (hele chippen = handling). Kan minimeres til en prikk.
 *  - `checkout`: brukeren er sjekket inn → kollapset statuslinje
 *    («Sjekket inn · {plass} · natt N»); trykk utvider til status + forleng-
 *    stepper + «Sjekk ut». Kan minimeres til en prikk — men ALDRI fjernes helt,
 *    så utsjekk alltid er ett trykk unna.
 *
 * Ren presentasjon; all data (natt-tall, dato, handlere) kommer fra App.
 */
export type CheckInOutPillProps =
  | { mode: 'checkin'; spotName: string; onCheckIn: () => void }
  | {
      mode: 'checkout';
      spotName: string;
      nightLabel: string;   // f.eks. «natt 2»
      endLabel: string;     // f.eks. «til 24. jul» (kan være tom)
      onOpen: () => void;
      onExtend: (days: number) => void;
      onCheckOut: () => void;
    };

type View = 'default' | 'expanded' | 'minimized';

const surface = 'bg-[var(--bg-surface)]/95 backdrop-blur-xl border border-brand/40 shadow-2xl';

const CheckInOutPill: React.FC<CheckInOutPillProps> = (props) => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('default');

  const minimizeBtn = (
    <button
      onClick={(e) => { e.stopPropagation(); setView('minimized'); }}
      aria-label={t('spot.pill.minimize', 'Minimer')}
      title={t('spot.pill.minimize', 'Minimer')}
      className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] active:scale-90 transition"
    >
      <X size={15} strokeWidth={2.5} />
    </button>
  );

  // Minimert: liten prikk (begge moduser). Alltid re-utvidbar → utsjekk aldri borte.
  if (view === 'minimized') {
    return (
      <button
        onClick={() => setView(props.mode === 'checkout' ? 'expanded' : 'default')}
        aria-label={t('spot.pill.expand', 'Vis innsjekk-status')}
        className={`w-11 h-11 rounded-full ${surface} flex items-center justify-center active:scale-95 transition-transform`}
      >
        <Tent size={18} className="text-emerald-400" />
      </button>
    );
  }

  if (props.mode === 'checkin') {
    return (
      <div className={`flex items-center gap-1 rounded-full ${surface} pl-3 pr-1.5 py-1 max-w-[calc(100vw-2rem)]`}>
        <button
          onClick={props.onCheckIn}
          className="flex items-center gap-2 min-h-11 active:opacity-60 transition-opacity min-w-0"
        >
          <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <MapPin size={14} className="text-emerald-400" />
          </span>
          <span className="text-tiny font-bold text-[var(--text-primary)] truncate">
            {t('spot.pill.check_in_here', { name: props.spotName || t('sidebar.unknown_place'), defaultValue: 'Sjekk inn på {{name}}?' })}
          </span>
        </button>
        {minimizeBtn}
      </div>
    );
  }

  // --- checkout ---
  const statusLine = props.endLabel ? `${props.nightLabel} · ${props.endLabel}` : props.nightLabel;

  if (view === 'expanded') {
    return (
      <div className={`w-[min(20rem,calc(100vw-2rem))] rounded-3xl ${surface} p-3 flex flex-col gap-2.5`}>
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <Tent size={16} className="text-emerald-400" />
          </span>
          <button
            onClick={props.onOpen}
            className="flex flex-col items-start min-w-0 flex-1 text-left active:opacity-60 transition-opacity"
          >
            <span className="text-3xs font-black uppercase tracking-widest text-emerald-400/90">
              {t('spot.pill.checked_in', 'Sjekket inn')}
            </span>
            <span className="text-sm font-bold text-[var(--text-primary)] truncate max-w-full">
              {props.spotName || t('sidebar.unknown_place')}
            </span>
          </button>
          <button
            onClick={() => setView('default')}
            aria-label={t('spot.pill.collapse', 'Skjul')}
            className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] active:scale-90 transition"
          >
            <ChevronDown size={16} strokeWidth={2.5} />
          </button>
        </div>
        <p className="text-2xs font-semibold text-[var(--text-secondary)] pl-1">{statusLine}</p>
        <ExtendStayControl variant="sheet" onExtend={props.onExtend} />
        <button
          onClick={props.onCheckOut}
          className="min-h-11 rounded-2xl bg-brand text-white text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <LogOut size={15} strokeWidth={3} />
          {t('spot.occupied.check_out', 'Sjekk ut')}
        </button>
      </div>
    );
  }

  // checkout collapsed (default): statuslinje, trykk = utvid
  return (
    <div className={`flex items-center gap-1 rounded-full ${surface} pl-2.5 pr-1.5 py-1 max-w-[calc(100vw-2rem)]`}>
      <button
        onClick={() => setView('expanded')}
        className="flex items-center gap-2 min-h-11 min-w-0 active:opacity-60 transition-opacity"
        aria-label={t('spot.pill.expand', 'Vis innsjekk-status')}
      >
        <span className="w-7 h-7 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
          <Tent size={14} className="text-emerald-400" />
        </span>
        <span className="flex flex-col items-start min-w-0 leading-tight text-left">
          <span className="text-3xs font-black uppercase tracking-widest text-emerald-400/90">
            {t('spot.pill.checked_in', 'Sjekket inn')}
          </span>
          <span className="text-2xs font-bold text-[var(--text-primary)] truncate max-w-full">
            {(props.spotName || t('sidebar.unknown_place'))}{props.nightLabel ? ` · ${props.nightLabel}` : ''}
          </span>
        </span>
        <ChevronUp size={14} className="text-[var(--text-muted)] shrink-0 ml-0.5" />
      </button>
      {minimizeBtn}
    </div>
  );
};

export default CheckInOutPill;
